(() => {
  const uiText = element => window.EasternI18n?.sourceText(element) ?? element?.textContent ?? "";
  const menus = new Map();
  const footers = new Map();
  let activeMenu = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initializeMenus(root = document) {
    for (const [button, controller] of menus) {
      if (!button.isConnected || !controller.menu.isConnected) controller.destroy();
    }
    const selector = '[data-brand-menu], [data-brand-contact]';
    const buttons = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) buttons.unshift(root);
    buttons.forEach(button => {
      if (menus.has(button)) return;
      const contact = button.hasAttribute('data-brand-contact');
      const kind = contact ? 'contact' : 'menu';
      const menu = document.getElementById(button.getAttribute('aria-controls'));
      const header = button.closest('header');
      const mobileMenu = window.matchMedia('(max-width: 640px)');
      const headerControls = header?.querySelector('.brand-header-inner');
      const closeButton = menu?.querySelector(`[data-brand-${kind}-close]`);
      const backdrop = header?.querySelector(`[data-brand-${kind}-backdrop]`);
      if (!menu || !header || !closeButton || !backdrop) return;
      const listeners = [];
      const inertBefore = new Map();
      const animations = new Set();
      let open = false;
      let bodyWasLocked = false;
      let motionVersion = 0;
      let restoreTarget = button;
      const listen = (target, type, handler) => {
        target.addEventListener(type, handler);
        listeners.push(() => target.removeEventListener(type, handler));
      };
      const cancelMotion = () => {
        motionVersion += 1;
        animations.forEach(animation => animation.cancel());
        animations.clear();
      };
      const hide = () => {
        menu.hidden = true;
        backdrop.hidden = true;
        menu.classList.remove('is-open', 'is-closing');
      };
      const animate = (showing, complete = () => {}) => {
        if (reducedMotion.matches || typeof menu.animate !== 'function') {
          complete();
          return;
        }
        const version = motionVersion;
        const offscreen = `translateX(${contact ? '' : '-'}100%)`;
        const options = { duration: contact ? (showing ? 300 : 220) : 1400, easing: contact ? 'cubic-bezier(.22,.61,.36,1)' : 'cubic-bezier(.74,0,0,1)', fill: 'both' };
        const slide = menu.animate(showing ? [{ transform: offscreen }, { transform: 'translateX(0)' }]
          : [{ transform: 'translateX(0)' }, { transform: offscreen }], options);
        const shade = backdrop.animate(showing ? [{ opacity: 0 }, { opacity: 1 }]
          : [{ opacity: 1 }, { opacity: 0 }], options);
        animations.add(slide);
        animations.add(shade);
        Promise.all([slide.finished, shade.finished]).then(() => {
          if (version !== motionVersion) return;
          complete();
          animations.forEach(animation => animation.cancel());
          animations.clear();
        }).catch(() => {}); // A new action can deliberately cancel an earlier transition.
      };
      const focusable = () => {
        const selector = 'a[href], button, input, select, textarea, [tabindex]';
        const candidates = [...(!contact && mobileMenu.matches && headerControls ? headerControls.querySelectorAll(selector) : []), ...menu.querySelectorAll(selector)];
        return [...new Set(candidates)].filter(element => !element.disabled && element.tabIndex >= 0
          && !element.closest('[hidden], [inert]') && element.getClientRects().length > 0);
      };
      const allowsFocus = element => menu.contains(element)
        || (!contact && mobileMenu.matches && headerControls?.contains(element));
      const focusStart = () => (focusable()[0] || closeButton).focus();
      const close = (restoreFocus = true, immediately = false) => {
        if (!open) {
          if (immediately) { cancelMotion(); hide(); }
          return;
        }
        open = false;
        cancelMotion();
        menu.classList.add('is-closing');
        menu.setAttribute('aria-hidden', 'true');
        menu.setAttribute('inert', '');
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', contact ? 'Contact us' : 'Open menu');
        inertBefore.forEach((value, element) => {
          if (value === null) element.removeAttribute('inert');
          else element.setAttribute('inert', value);
        });
        inertBefore.clear();
        if (!bodyWasLocked) document.body.classList.remove('brand-menu-open');
        if (activeMenu?.menu === menu) activeMenu = null;
        if (restoreFocus) {
          const target = restoreTarget?.isConnected && restoreTarget.getClientRects().length ? restoreTarget : button;
          if (target.isConnected && target.getClientRects().length) target.focus();
        }
        if (immediately) hide();
        else animate(false, hide);
      };
      const show = () => {
        if (open) return;
        document.dispatchEvent?.(new CustomEvent('brand:panel-opening', { detail: { panel: menu } }));
        activeMenu?.close(false, true);
        // Also finish a different drawer that is still visually closing.
        menus.forEach(other => { if (other !== controller) other.close(false, true); });
        cancelMotion();
        open = true;
        activeMenu = controller;
        restoreTarget = document.activeElement?.matches('[data-brand-contact-announcement]') ? document.activeElement : button;
        menu.setAttribute('aria-modal', !contact && mobileMenu.matches ? 'false' : 'true');
        bodyWasLocked = document.body.classList.contains('brand-menu-open');
        document.body.classList.add('brand-menu-open');
        [...document.querySelectorAll('main, footer'), ...document.querySelectorAll('.brand-mobile-search')].forEach(element => {
          if (element.contains(menu)) return;
          inertBefore.set(element, element.getAttribute('inert'));
          element.setAttribute('inert', '');
        });
        menu.hidden = false;
        menu.classList.remove('is-closing');
        menu.classList.add('is-open');
        menu.removeAttribute('aria-hidden');
        menu.removeAttribute('inert');
        backdrop.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        button.setAttribute('aria-label', contact ? 'Close contact panel' : 'Close menu');
        if (!contact && mobileMenu.matches) focusStart();
        else closeButton.focus();
        animate(true);
      };
      const controller = { menu, close, destroy() {
        close(false, true);
        listeners.forEach(remove => remove());
        menus.delete(button);
      } };
      hide();
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', contact ? 'Contact us' : 'Open menu');
      [button, closeButton, backdrop].forEach(control => { control.type = 'button'; });
      listen(button, 'click', () => open ? close() : show());
      listen(closeButton, 'click', () => close());
      listen(backdrop, 'click', () => close());
      listen(menu, 'click', event => {
        const link = event.target.closest('a[href]');
        if (link && menu.contains(link)) close(false, true);
      });
      listen(document, 'brand:panel-opening', event => {
        if (event.detail?.panel !== menu) close(false, true);
      });
      listen(mobileMenu, 'change', () => {
        if (!open) return;
        menu.setAttribute('aria-modal', !contact && mobileMenu.matches ? 'false' : 'true');
        if (!allowsFocus(document.activeElement) || !document.activeElement?.getClientRects().length) focusStart();
      });
      listen(document, 'keydown', event => {
        if (!open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== 'Tab') return;
        const controls = focusable();
        const first = controls[0] || closeButton;
        const last = controls[controls.length - 1] || closeButton;
        const focused = document.activeElement;
        if (!allowsFocus(focused) || (event.shiftKey && focused === first)
          || (!event.shiftKey && focused === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      });
      listen(document, 'focusin', event => {
        if (open && !allowsFocus(event.target)) focusStart();
      });
      menus.set(button, controller);
    });
  }

  function initializeFooters(root = document) {
    for (const [details, controller] of footers) {
      if (!details.isConnected) controller.destroy();
    }
    root.querySelectorAll('[data-brand-footer-accordion]').forEach(details => {
      if (footers.has(details)) return;
      const summary = details.querySelector('summary');
      if (!summary) return;
      const mobile = window.matchMedia('(max-width: 640px)');
      let mobileOpen = false;
      const update = () => {
        details.open = mobile.matches ? mobileOpen : true;
        summary.tabIndex = mobile.matches ? 0 : -1;
        if (mobile.matches) summary.removeAttribute('aria-disabled');
        else summary.setAttribute('aria-disabled', 'true');
      };
      const click = event => { if (!mobile.matches) event.preventDefault(); };
      const toggle = () => {
        if (mobile.matches) mobileOpen = details.open;
        else if (!details.open) details.open = true;
      };
      summary.addEventListener('click', click);
      details.addEventListener('toggle', toggle);
      mobile.addEventListener('change', update);
      update();
      footers.set(details, { destroy() {
        summary.removeEventListener('click', click);
        details.removeEventListener('toggle', toggle);
        mobile.removeEventListener('change', update);
        footers.delete(details);
      } });
    });
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-native-brand-form]').forEach(marker => {
      const form = marker.closest('form');
      if (form) form.dataset.brandForm = '';
    });
    initializeMenus(root);
    initializeFooters(root);
    root.querySelectorAll('[data-brand-form]').forEach(form => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      const topic = form.querySelector('[data-topic]');
      const reference = form.querySelector('[data-order-field]');
      const interest = form.querySelector('[data-interest]');
      const interestField = form.querySelector('[data-interest-field]');
      const piece = form.querySelector('[data-piece-inquiry]');
      const pieceField = form.querySelector('[data-piece-inquiry-field]');
      const pieceReference = form.querySelector('[data-piece-inquiry-reference]');
      const pieceLink = form.querySelector('[data-piece-inquiry-link]');
      const preferences = form.querySelector('[data-inquiry-preferences]');
      const size = form.querySelector('[data-inquiry-size]');
      const occasion = form.querySelector('[data-inquiry-occasion]');
      const message = form.querySelector('textarea[name="contact[body]"]');
      const context = form.querySelector('[data-inquiry-context]');
      const contextTitle = form.querySelector('[data-inquiry-context-title]');
      const contextCopy = form.querySelector('[data-inquiry-context-copy]');
      const back = document.querySelector('.brand-inquiry-back a');
      const isPartner = Boolean(form.querySelector('[name="contact[Company or Channel]"]'));
      const emailResult = form.querySelector('[data-email-compose-result]');
      const emailBody = emailResult?.querySelector('[data-email-compose-body]');
      const emailOpen = emailResult?.querySelector('[data-email-compose-open]');
      const emailCopy = emailResult?.querySelector('[data-email-compose-copy]');
      const emailStatus = emailResult?.querySelector('[data-email-compose-status]');
      let emailVersion = 0;
      const invalidateEmail = () => {
        if (!emailResult || emailResult.hidden) return;
        emailVersion += 1;
        emailResult.hidden = true;
        emailOpen?.removeAttribute('href');
        if (emailBody) emailBody.value = '';
        const status = form.querySelector('[data-form-status]');
        if (status) { status.hidden = false; status.textContent = 'Your details changed. Choose Prepare email to update your message. Nothing has been sent.'; }
      };
      const prepareEmail = (focus = true) => {
        const t = value => window.EasternI18n?.t(value) ?? value;
        const recipient = (form.dataset.emailCompose || '').trim();
        if (!emailResult || !emailBody || !emailOpen || !emailStatus || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recipient)) {
          const status = form.querySelector('[data-form-status]');
          if (status) { status.hidden = false; status.textContent = 'Email preparation is unavailable. Please use the email contact link on this page. Nothing has been sent.'; status.focus(); }
          return;
        }
        const values = new FormData(form);
        const parts = [t('Eastern Jewelry inquiry')];
        if (contextTitle?.textContent.trim()) parts.push(t('Inquiry context:') + ' ' + t(uiText(contextTitle).trim()));
        const labels = { name: 'Full name', email: 'Reply-to email', body: 'Message' };
        for (const [name, raw] of values.entries()) {
          const match = name.match(/^contact\[([^\]]+)\]$/);
          if (!match || typeof raw !== 'string' || !raw.trim()) continue;
          const field = match[1];
          if (['First name', 'Last name'].includes(field) && values.get('contact[name]')) continue;
          if (field === 'Jewelry design reference' && values.get('contact[Jewelry design]')) continue;
          const control = form.elements.namedItem(name);
          const value = control?.tagName === 'SELECT'
            ? Array.from(control.selectedOptions).filter(option => !option.disabled && option.value).map(option => t(uiText(option).trim())).join(', ')
            : field === 'Selected designs' ? raw.trim().split('\n').map(t).join('\n') : raw.trim();
          if (value) parts.push(`${t(labels[field] || field)}:${value.includes('\n') || field === 'body' ? '\n' : ' '}${value}`);
        }
        emailBody.value = parts.join('\n\n');
        const subject = (t('Eastern Jewelry') + ' — ' + t(topic?.value || 'Jewelry inquiry')).replace(/[\r\n]+/g, ' ').slice(0, 160);
        const base = `mailto:${recipient}?subject=${encodeURIComponent(subject)}`;
        const fullLink = base + '&body=' + encodeURIComponent(emailBody.value);
        // Mail clients have different URL limits. Keep the complete text visible
        // for copying instead of silently truncating a longer inquiry.
        const copyFirst = fullLink.length > 1800;
        emailOpen.href = copyFirst ? base : fullLink;
        emailResult.dataset.composeRequiresPaste = String(copyFirst);
        emailStatus.textContent = copyFirst
          ? 'This message is long. Copy the full email text, open your email app, then paste it into the message. Nothing has been sent.'
          : 'Nothing has been sent. Open your email app to review and send this message.';
        emailVersion += 1;
        const status = form.querySelector('[data-form-status]');
        if (status) status.hidden = true;
        emailResult.hidden = false;
        if (focus) emailResult.focus();
      };
      document.addEventListener('brand:languagechange', () => { if (emailResult && !emailResult.hidden) prepareEmail(false); });
      form.addEventListener('input', event => { if (!emailResult?.contains(event.target)) invalidateEmail(); });
      form.addEventListener('change', event => { if (!emailResult?.contains(event.target)) invalidateEmail(); });
      emailCopy?.addEventListener('click', async () => {
        if (!emailBody?.value || emailResult.hidden) return;
        const version = emailVersion;
        emailCopy.disabled = true;
        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(emailBody.value);
          if (version !== emailVersion) return;
          emailStatus.textContent = 'Email text copied. Paste it into your email app, review it, then send it. Nothing has been sent by this website.';
        } catch (_) {
          if (version !== emailVersion) return;
          emailBody.focus(); emailBody.select();
          emailStatus.textContent = 'Automatic copying is unavailable. The email text is selected; copy it manually, then paste it into your email app. Nothing has been sent.';
        } finally { emailCopy.disabled = false; }
      });
      const allowedOccasions = new Set(['everyday', 'gift', 'occasion']);
      const serviceContexts = {
        'jewelry-consultation': ['Jewelry Consultation', 'Share the style or color you enjoy, your budget and currency, and your destination or occasion date.', 'I am interested in… My budget and currency are… My delivery destination and occasion date are…'],
        'personal-gifting': ['Gifts & Occasions', 'Tell us about the recipient, the occasion date, budget and delivery destination. Include any sizing or presentation requests.', 'The gift is for… The occasion date is… My budget and destination are…'],
        'customization': ['Customization', 'Describe your idea, known measurements and material preferences. Include your budget and desired timing so the actual scope can be discussed.', 'My idea is… The measurements or materials I have in mind are… My budget and timing are…'],
        'assessment-documentation': ['Assessment & Documentation', 'Identify the actual piece and the question you need to resolve. Mention any existing documents and decision date.', 'The piece or order reference is… I would like to clarify… Existing documents or my decision date are…'],
        'jewelry-aftercare': ['Jewelry Aftercare', 'Include your order reference, what happened and when, and your location. Send jewelry only after receiving confirmed instructions.', 'My order reference is… The issue began… I am located in…']
      };
      const partnerContexts = {
        'Retail & Agency': ['retail-agency', 'Introduce your business, customer market, selling channels and proposed responsibilities or support needs.', 'My business and market are… Our channels are… I would like to discuss…'],
        'Wholesale': ['wholesale', 'Share the jewelry categories, approximate quantities, budget and currency, destination and required timing.', 'We are sourcing… Approximate quantities and budget are… The destination and timing are…'],
        'Creators & Marketing': ['creator-partnerships', 'Share your public channel, audience and market, project format, timing and proposed usage or commercial terms.', 'My channel and audience are… The proposed project and timing are… I would like to discuss these terms…'],
        'Corporate Projects': ['corporate-projects', 'Describe the purpose, recipients, quantity range, budget, presentation requests, destination and desired arrival date.', 'The project is for… Quantity range and budget are… Presentation, destination and arrival date are…']
      };
      const params = new URLSearchParams(location.search);
      const selection = form.querySelector('[data-inquiry-selection]');
      const selectionList = form.querySelector('[data-inquiry-selection-list]');
      const selectionValue = form.querySelector('[data-inquiry-selection-value]');
      const selectionAdd = form.querySelector('[data-inquiry-selection-add]');
      const selectionStatus = form.querySelector('[data-inquiry-selection-status]');
      const designOptions = new Map(Array.from(selectionAdd?.options || []).filter(option => option.value).map(option => [option.value, option]));
      const incomingIds = (params.get('pieces') || '').slice(0, 3000).split(',').filter(Boolean);
      const selectedIds = new Set(incomingIds.filter(id => designOptions.has(id)).slice(0, 36));
      const isSelectionInquiry = incomingIds.length > 0;
      const category = form.querySelector('[data-inquiry-category]');
      const color = form.querySelector('[data-inquiry-color]');
      const issue = form.querySelector('[data-inquiry-issue]');
      const informationRequest = form.querySelector('[data-inquiry-request]');
      const requestCopies = {
        specifications: 'Please confirm the actual piece photos, metal and stone description, measurements, included items, total and currency before payment.',
        documents: 'Please identify any available report, its issuer and report number, and which actual piece it covers. A document is not assumed to be available for every design.',
        'actual-photos': 'Please share clear photos of the actual piece, including its setting, reverse, fastening and any condition details that matter.',
        packaging: 'Please confirm exactly which box, pouch, chain, accessories or gift presentation are included, and any additional cost.'
      };
      if (informationRequest && Object.hasOwn(requestCopies, params.get('request'))) informationRequest.value = params.get('request');
      const issueCopy = {
        'payment-link': 'Tell us whether the link is missing, expired, or shows an incorrect amount, currency or order reference. Do not paste the private link here.',
        'payment-pending': 'Do not pay again while the first attempt is being checked. Include the attempted amount, currency and approximate time; never send card numbers, security codes or one-time codes.',
        'delivery': 'Include your order reference, dispatch date if known and the latest tracking message. For live updates, use View your order in your confirmation email.',
        'change-cancel': 'Describe the requested change. Our team must check dispatch before agreeing to a change or cancellation. A submitted request does not itself cancel an order.',
        'quality': 'Describe the product issue, when it was noticed and the delivery date. Keep packaging and photographs; our team will provide a verified channel for images. Do not return jewelry before receiving instructions.',
        'aftercare': 'Describe the piece, the care or repair required and your location. Feasibility, any cost and timing are confirmed after review.'
      };
      [category, color].forEach(control => {
        const value = params.get(control === category ? 'category' : 'color');
        if (control && Array.from(control.options).some(option => option.value === value)) control.value = value;
      });
      if (issue && Object.hasOwn(issueCopy, params.get('issue'))) issue.value = params.get('issue');
      const urlTopic = params.get('topic');
      if (topic && urlTopic && Array.from(topic.options).some(option => option.value === urlTopic)) topic.value = urlTopic;
      if (topic && issue?.value) topic.value = issue.value.startsWith('payment-') ? 'Payment question' : 'Existing order / aftercare';
      const sourceInterest = interest && Array.from(interest.options).find(option => option.value && option.value === params.get('interest'));
      if (sourceInterest) interest.value = sourceInterest.value;
      const sourcePiece = piece && Array.from(piece.options).find(option => option.value && option.value === params.get('piece'));
      if (sourcePiece) piece.value = sourcePiece.value;
      if (size) {
        size.value = (params.get('size') || '').trim().slice(0, 100);
        if (/^(?:Known US [\d.]+|(?:Inside diameter|Finger circumference) [\d.]+ mm); (?:[\d.]+ mm diameter|US [\d.–]+) \(reference only\)$/.test(size.value)) size.dataset.i18nValue = size.value;
      }
      if (occasion) occasion.value = allowedOccasions.has(params.get('occasion')) ? params.get('occasion') : '';
      if (preferences && (size?.value || occasion?.value)) preferences.open = true;
      // A hosted product can provide a plain-text title; never turn it into HTML.
      const hostedItem = params.get('item');
      if (!sourcePiece && hostedItem && message && !message.value && topic?.value !== 'Payment question') {
        message.value = 'I would like to ask about: ' + hostedItem.slice(0, 200) + '\n';
        message.dataset.i18nValue = message.value;
      }
      const showField = (field, visible) => {
        if (!field) return;
        field.hidden = !visible;
        field.querySelectorAll('input, select, textarea').forEach(control => { control.disabled = !visible; });
      };
      const update = () => {
        invalidateEmail();
        const currentTopic = topic?.value || '';
        const payment = currentTopic === 'Payment question';
        const orderSupport = payment || currentTopic === 'Existing order / aftercare';
        showField(reference, ['Payment question', 'Existing order / aftercare'].includes(currentTopic));
        showField(interestField, !orderSupport);
        showField(pieceField, !orderSupport && !isSelectionInquiry);
        showField(preferences, !orderSupport);
        showField(selection, !orderSupport && isSelectionInquiry);
        showField(form.querySelector('[data-inquiry-style-fields]'), !orderSupport);
        showField(form.querySelector('[data-inquiry-request-field]'), !orderSupport);
        const requestCopy = form.querySelector('[data-inquiry-request-copy]');
        if (requestCopy) requestCopy.textContent = !orderSupport ? requestCopies[informationRequest?.value] || '' : '';
        showField(form.querySelector('[data-inquiry-order-tools]'), orderSupport);
        if (issue) Array.from(issue.options).forEach(option => { option.disabled = Boolean(option.value) && (payment !== option.value.startsWith('payment-')); });
        showField(form.querySelector('[data-inquiry-payment-status-field]'), payment && issue?.value === 'payment-pending');
        showField(form.querySelector('[data-inquiry-resolution-field]'), orderSupport && !payment && ['quality', 'aftercare'].includes(issue?.value));
        const orderHint = form.querySelector('[data-inquiry-order-hint]');
        if (orderHint) orderHint.textContent = orderSupport ? (issueCopy[issue?.value] || 'Choose the subject above and include your order reference when available.') : '';
        const selected = piece?.selectedOptions[0];
        const pieceRoute = !orderSupport && selected?.dataset.pieceRoute;
        const pieceLabel = pieceRoute ? uiText(selected).trim() : '';
        if (pieceReference) pieceReference.value = pieceRoute ? pieceLabel : '';
        const preferenceParams = new URLSearchParams();
        const sizeValue = (size?.value || '').trim().slice(0, 100);
        if (sizeValue) preferenceParams.set('size', sizeValue);
        if (allowedOccasions.has(occasion?.value)) preferenceParams.set('occasion', occasion.value);
        const returnPiece = pieceRoute ? pieceRoute + (preferenceParams.size ? '?' + preferenceParams.toString() : '') : '';
        if (pieceLink) {
          pieceLink.href = returnPiece || '/pages/jewelry';
          pieceLink.textContent = pieceRoute ? 'View ' + pieceLabel : 'Browse all jewelry';
        }
        const selectedInterest = !orderSupport && interest?.selectedOptions[0];
        const service = selectedInterest?.value ? serviceContexts[selectedInterest.value] : null;
        const partner = isPartner ? partnerContexts[currentTopic] : null;
        let heading = 'Your jewelry inquiry';
        let prompt = 'Share the category or color you have in mind, your budget and currency, and any destination or timing requirements.';
        let placeholder = 'I am interested in… My budget and currency are… My destination or timing requirements are…';
        let returnRoute = isPartner ? '/pages/partners' : '/pages/client-care';
        let returnLabel = isPartner ? 'Partnerships' : 'Client Care';
        if (payment) {
          heading = 'Your payment question';
          prompt = 'Include your order reference and the payment question. Mention the amount, currency or status where relevant; do not send private checkout links or payment security details.';
          placeholder = 'My order reference is… The amount or currency is… My payment question is…';
          returnRoute = '/pages/client-payment'; returnLabel = 'Payment Guide';
        } else if (partner) {
          heading = currentTopic; prompt = partner[1]; placeholder = partner[2];
          returnRoute = '/pages/' + partner[0]; returnLabel = currentTopic;
        } else if (service) {
          [heading, prompt, placeholder] = service;
          returnRoute = '/pages/' + selectedInterest.value; returnLabel = heading;
        } else if (currentTopic === 'Existing order / aftercare') {
          [heading, prompt, placeholder] = serviceContexts['jewelry-aftercare'];
          returnRoute = '/pages/jewelry-aftercare'; returnLabel = 'Jewelry Aftercare';
        } else if (selectedInterest?.value) {
          heading = uiText(selectedInterest).trim();
          returnRoute = '/pages/' + selectedInterest.value; returnLabel = heading;
        } else if (isPartner) {
          heading = 'Your partnership inquiry';
          prompt = 'Choose a partnership type and introduce your business or channel, market and proposal.';
          placeholder = 'My business or channel is… My market and proposal are…';
        }
        if (pieceRoute) {
          heading = pieceLabel;
          returnRoute = returnPiece; returnLabel = pieceLabel;
        }
        if (isSelectionInquiry && !orderSupport) {
          heading = `${selectedIds.size} selected design${selectedIds.size === 1 ? '' : 's'}`;
          returnRoute = '/pages/saved-pieces?selection=' + encodeURIComponent([...selectedIds].join(','));
          returnLabel = 'your selection';
          if (selectedIds.size === 0) prompt = 'No designs are currently selected. You may add a design below or send a general jewelry inquiry.';
        }
        if (!orderSupport && Object.hasOwn(requestCopies, informationRequest?.value)) {
          prompt = requestCopies[informationRequest.value];
          placeholder = 'I would like to confirm… My specific questions are…';
        }
        if (context && contextTitle && contextCopy) {
          context.hidden = false; contextTitle.textContent = heading; contextCopy.textContent = prompt;
        }
        if (message) message.placeholder = placeholder;
        if (back) { back.href = returnRoute; back.setAttribute('aria-label', 'Back to ' + returnLabel); }
      };
      const renderSelection = () => {
        if (!selectionList) return;
        selectionList.replaceChildren();
        selectedIds.forEach(id => {
          const option = designOptions.get(id);
          const row = document.createElement('li');
          const image = document.createElement('img'); image.src = option.dataset.image; image.alt = ''; image.width = 64; image.height = 80;
          const link = document.createElement('a'); link.href = option.dataset.route; link.textContent = uiText(option);
          const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = 'Remove'; remove.setAttribute('aria-label', 'Remove ' + uiText(option) + ' from inquiry');
          remove.addEventListener('click', () => { selectedIds.delete(id); renderSelection(); selectionStatus.textContent = uiText(option) + ' removed.'; update(); selectionAdd?.focus(); });
          row.append(image, link, remove); selectionList.append(row);
        });
        if (selectionValue) selectionValue.value = [...selectedIds].map(id => uiText(designOptions.get(id)).trim()).join('\n');
        const returnLink = form.querySelector('[data-inquiry-selection-back]');
        if (returnLink) returnLink.href = '/pages/saved-pieces?selection=' + encodeURIComponent([...selectedIds].join(','));
      };
      renderSelection();
      if (incomingIds.some(id => !designOptions.has(id)) && selectionStatus) selectionStatus.textContent = 'Some designs in this link were not recognized and were omitted. Please review the list.';
      form.querySelector('[data-inquiry-selection-add-button]')?.addEventListener('click', () => {
        const id = selectionAdd?.value;
        if (!designOptions.has(id)) { selectionStatus.textContent = 'Choose a design first.'; selectionAdd?.focus(); return; }
        if (selectedIds.has(id)) { selectionStatus.textContent = 'This design is already in your inquiry.'; return; }
        selectedIds.add(id); renderSelection(); selectionStatus.textContent = uiText(designOptions.get(id)) + ' added.'; update();
      });
      topic?.addEventListener('change', () => {
        if (issue?.value && (!['Payment question', 'Existing order / aftercare'].includes(topic.value)
          || (topic.value === 'Payment question') !== issue.value.startsWith('payment-'))) issue.value = '';
        update();
      });
      issue?.addEventListener('change', update);
      informationRequest?.addEventListener('change', update);
      interest?.addEventListener('change', () => {
        if (interest.value === 'jewelry-aftercare' && topic?.value === 'New jewelry advice') topic.value = 'Existing order / aftercare';
        update();
      });
      piece?.addEventListener('change', update);
      size?.addEventListener('input', update);
      occasion?.addEventListener('change', update);
      update();
      form.addEventListener('submit', event => {
        if (size) size.value = size.value.trim().slice(0, 100);
        if (occasion && !allowedOccasions.has(occasion.value)) occasion.value = '';
        const fullName = form.querySelector('[data-brand-full-name]');
        if (fullName) fullName.value = [form.querySelector('[data-brand-first-name]')?.value, form.querySelector('[data-brand-last-name]')?.value].map(value => (value || '').trim()).filter(Boolean).join(' ');
        if (form.hasAttribute('data-email-compose')) {
          event.preventDefault();
          prepareEmail();
          return;
        }
        if (form.dataset.preview === 'true') {
          event.preventDefault();
          const status = form.querySelector('[data-form-status]');
          status.hidden = false;
          status.textContent = 'Preview only. Your form is valid, but no message has been sent. Email delivery will be connected in Shopify.';
          status.focus();
          return;
        }
        // Native Shopify submission owns success/failure; do not synthesize it.
        const submit = form.querySelector('[type="submit"]');
        if (submit) { submit.disabled = true; submit.textContent = 'Sending…'; }
      });
      if (form.hasAttribute('data-email-compose')) {
        // Release the fail-closed HTML only after the submit interceptor exists.
        // If either script or CSS fails to load, personal details cannot fall
        // through to a default GET request in the address bar or access logs.
        form.querySelector('[data-email-compose-fields]')?.removeAttribute('disabled');
        form.dataset.emailComposeReady = 'true';
        form.hidden = false;
      }
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [details, controller] of footers) {
      if (event.target === details || event.target.contains(details)) controller.destroy();
    }
    for (const [button, controller] of menus) {
      if (event.target === button || event.target.contains(button)) controller.destroy();
    }
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-brand-form] [type="submit"]').forEach(button => {
      button.disabled = false;
      button.textContent = button.closest('[data-email-compose]') ? 'Prepare email' : 'Send Inquiry';
    });
  });
})();
